import React, { useMemo } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { StreamingSource } from 'fym-shared/src/music24/streaming';
import { TypedMemo } from '../../../common/types';
import { sourceLook } from '../streamingSources';
import { formatDay } from '../streamingFormat';
import { streamingStateMessages as messages } from '../streamingStateMessages';
import { withBrandWord } from '../../components/primitives/BrandWord';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the whole screen; each part uses its own slice of it */
import styles from '../Streaming.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface StreamingStateLinesProps {
  sources: StreamingSource[];
  /**
   * The line "Each section shows what your connected sources report" with the link to Catalog: a
   * source is connected and at least one of Stores, Countries and Earnings has no source.
   */
  showSourcesNote: boolean;
  onOpenCatalog: () => void;
}

/**
 * The lines under the chips (design f3, f5): one info line for each source in its first read, one
 * warning line for each source that is signed out, with the day its numbers stop, one plain line
 * for each source that the artist disconnected (its numbers stay as history), and the line that
 * says each section shows what the connected sources report. Each action is a link to Catalog.
 */
const StreamingStateLinesComponent: React.FC<StreamingStateLinesProps> = ({
  sources,
  showSourcesNote,
  onOpenCatalog,
}) => {
  const intl = useIntl();
  const reading = useMemo(() => sources.filter((source) => source.state === 'firstRead'), [sources]);
  const signedOut = useMemo(() => sources.filter((source) => source.state === 'reconnect'), [sources]);
  const disconnected = useMemo(() => sources.filter((source) => source.state === 'disconnected'), [sources]);

  return (
    <>
      {reading.map((source) => (
        <div key={source.integrationType} className={clsx(styles.line, styles.lineInfo)} role="status">
          {intl.formatMessage(messages.firstRead, { source: sourceLook(source.integrationType).name })}
        </div>
      ))}
      {signedOut.map((source) => (
        <div key={source.integrationType} className={clsx(styles.line, styles.lineWarn)} role="alert">
          <span>
            {intl.formatMessage(messages.reconnectLine, {
              source: sourceLook(source.integrationType).name,
              date: source.stoppedAt === null ? '—' : formatDay(intl, source.stoppedAt),
            })}
          </span>
          <button type="button" className={styles.linkButton} onClick={onOpenCatalog}>
            {intl.formatMessage(messages.reconnectLink)}
          </button>
        </div>
      ))}
      {disconnected.map((source) => (
        <div
          key={source.integrationType}
          className={styles.line}
          data-testid={`streaming-disconnected-${source.integrationType}`}
        >
          <span>
            {withBrandWord(
              intl.formatMessage(messages.disconnectedLine, {
                source: sourceLook(source.integrationType).name,
                date: source.stoppedAt === null ? '—' : formatDay(intl, source.stoppedAt),
              })
            )}
          </span>
          <button type="button" className={styles.linkButton} onClick={onOpenCatalog}>
            {intl.formatMessage(messages.connectAgainLink)}
          </button>
        </div>
      ))}
      {showSourcesNote && sources.length > 0 ? (
        <div className={styles.line} data-testid="streaming-sources-note">
          <span>{intl.formatMessage(messages.sourcesNote)}</span>
          <button type="button" className={styles.linkButton} onClick={onOpenCatalog}>
            {intl.formatMessage(messages.connectSource)}
          </button>
        </div>
      ) : null}
    </>
  );
};

export const StreamingStateLines = TypedMemo(StreamingStateLinesComponent);
